import { useState } from "react";
import { Lock, Mail, Clock, ShieldCheck, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { RenewAccessDialog } from "@/components/admin/RenewAccessDialog";
import {
  MODULE_LABELS,
  MODULE_DESCRIPTIONS,
  type ModuleKey,
} from "@/lib/modules";

interface Props {
  module: ModuleKey;
  /** When provided AND in the past, renders the expired-access variant. */
  expiredAt?: string | null;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/**
 * Lock screen shown when a module is disabled (or expired) for the
 * current organisation. Administrators can renew access in place;
 * everyone else sees the contact-administrator message.
 */
export function ModuleLocked({ module, expiredAt }: Props) {
  const { isAdmin, organisation, assessmentAccess, refreshProfile } = useAuth();
  const [renewOpen, setRenewOpen] = useState(false);

  const label = MODULE_LABELS[module];
  const description = MODULE_DESCRIPTIONS[module];
  const isExpired =
    !!expiredAt && new Date(expiredAt).getTime() <= Date.now();
  const canRenew =
    isAdmin && module === "assessment" && !!organisation?.id;

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-xl w-full border border-border bg-card p-10 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-6">
          {isExpired ? (
            <Clock className="h-5 w-5 text-muted-foreground" />
          ) : (
            <Lock className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {isExpired ? "Access expired" : "Module locked"}
        </p>
        <h1 className="mt-2 text-2xl font-semibold text-foreground">
          {isExpired ? `${label} access has expired` : `${label} is not enabled`}
        </h1>

        {module === "assessment" && assessmentAccess.isDemo && (
          <div className="mt-3 flex justify-center">
            <Badge variant="outline" className="gap-1.5">
              <FlaskConical className="h-3 w-3" />
              Demo / Test access
            </Badge>
          </div>
        )}

        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>

        {organisation?.name && (
          <p className="mt-4 text-xs text-muted-foreground">
            Organisation: <span className="text-foreground">{organisation.name}</span>
            {expiredAt && <> · Expiry: {formatDate(expiredAt)}</>}
          </p>
        )}

        <p className="mt-4 text-sm text-muted-foreground">
          {isExpired
            ? `Your access expired on ${formatDate(expiredAt!)}. ${
                canRenew
                  ? "As an administrator you can renew it below."
                  : "Please contact your administrator to renew."
              }`
            : canRenew
              ? "Access is currently disabled. As an administrator you can enable and renew it below."
              : "Assessment access is currently disabled. Please contact admin."}
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {canRenew && (
            <Button size="sm" onClick={() => setRenewOpen(true)}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Renew access
            </Button>
          )}
          <Button asChild variant={canRenew ? "outline" : "default"} size="sm">
            <a href="mailto:support@safetracker.ai?subject=Request%20to%20enable%20module">
              <Mail className="mr-2 h-4 w-4" />
              Contact administrator
            </a>
          </Button>
        </div>
      </div>

      {canRenew && organisation && (
        <RenewAccessDialog
          open={renewOpen}
          onOpenChange={setRenewOpen}
          organisationId={organisation.id}
          organisationName={organisation.name}
          enabled={!isExpired}
          expiresAt={assessmentAccess.expiresAt}
          source={assessmentAccess.isDemo ? "demo" : "standard"}
          onUpdated={() => {
            void refreshProfile();
          }}
        />
      )}
    </div>
  );
}

export default ModuleLocked;
